/** Ed25519 through WebCrypto. The device private key is never extractable. */

/**
 * WebCrypto's `BufferSource` insists on a `Uint8Array<ArrayBuffer>`, while
 * every byte string on this wire is a plain `Uint8Array`, which TypeScript
 * reads as `Uint8Array<ArrayBufferLike>` because the buffer could in principle
 * be a `SharedArrayBuffer`. None of ours is: they come from a CBOR decoder or
 * a fresh allocation. The assertion is here, once, rather than at each of the
 * five call sites that would otherwise need it.
 */
export const asBufferSource = (bytes: Uint8Array): BufferSource => bytes as BufferSource;

export async function hasEd25519Support(): Promise<boolean> {
  try {
    await crypto.subtle.generateKey("Ed25519", false, ["sign", "verify"]);
    return true;
  } catch {
    return false;
  }
}

export async function createDeviceKey(): Promise<CryptoKeyPair> {
  return (await crypto.subtle.generateKey("Ed25519", false, ["sign", "verify"])) as CryptoKeyPair;
}

export async function signWithDevice(key: CryptoKey, bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.sign("Ed25519", key, asBufferSource(bytes)));
}

/** An Ed25519 public key is 32 bytes. `server/app.py` spells the same literal. */
const PUBLIC_KEY_LEN = 32;

/**
 * The one door both public keys come through: the issuer key out of the trust
 * store, and the device key out of the issuer-signed attributes.
 *
 * WebCrypto throws on anything that is not exactly 32 bytes, and both callers
 * are inside `verifyPresentation`, whose contract is that a refusal comes back
 * as a reason rather than as an exception. The guard therefore lands here,
 * once, and failure is `null`. Importing one of the two keys past this
 * function is how that guard ends up needing to be written twice.
 *
 * The `catch` is not reachable under Node, which accepts any 32 bytes as a
 * point and lets `verify` return false; a browser may reject a non-canonical
 * one instead, and the browser is what this ships on.
 */
export async function importPublicKey(raw: Uint8Array): Promise<CryptoKey | null> {
  if (raw.length !== PUBLIC_KEY_LEN) return null;
  try {
    return await crypto.subtle.importKey("raw", asBufferSource(raw), "Ed25519", true, ["verify"]);
  } catch {
    return null;
  }
}

export async function verifyEd25519(
  key: CryptoKey, signature: Uint8Array, bytes: Uint8Array,
): Promise<boolean> {
  return crypto.subtle.verify(
    "Ed25519", key, asBufferSource(signature), asBufferSource(bytes),
  );
}
